import "./polyfills/processPolyfill";
// Must run before any @react-pdf/renderer code so the global `Buffer` exists
// when fontkit subsets fonts. Keep this near the top of the entrypoint.
import "./utils/bufferPolyfill";
// Installs the global axios response interceptor that surfaces backend error
// messages via toast. Import before any module that fires HTTP requests.
import "./services/ApiCall";

/** importing libraries  **/
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import * as Sentry from "@sentry/react";
import { ConfigProvider } from "antd";

/** importing dependencies  **/
import { store } from "./redux/store";
import App from "./App";
import "./styles/index.scss";
import "antd/dist/reset.css";
import "react-toastify/dist/ReactToastify.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { configEnv } from "./services/constant";
import { AuditFormProvider } from "./context/context";
import {
    installIgnoredPromiseRejectionSuppressor,
    isIgnoredSentryEvent,
} from "./utils/sentryNoise";
import { scrubSentryLog } from "./utils/sentryLogScrub";
import {
    SENTRY_BEARER_URL_PATTERNS,
    SENTRY_REPLAY_BLOCK_SELECTORS,
    isSentryBearerUrl,
    isSentryReplayOnBearerPage,
    scrubSentryBreadcrumbUrls,
    scrubSentryEventUrls,
    scrubSentryLogUrls,
    scrubSentryReplayEventUrls,
    scrubSentryReplayRecordingEvent,
    scrubSentrySpanUrls,
} from "./utils/sentryUrlScrub";
import { installChunkErrorRecovery } from "./utils/chunkReload";

installIgnoredPromiseRejectionSuppressor();
// Must run before any lazy route can be requested: a tab that was open across a
// deploy holds the previous build's asset hashes, and this reloads it onto the
// current build instead of dead-ending on "Unable to preload CSS".
installChunkErrorRecovery();

// Session Replay records outside the hooks in Sentry.init: the page URLs on
// each replay, the page snapshots (rrweb writes the page URL into each) and
// the requests it saw. The replay options below mask what the SDK lets us
// rewrite. A snapshot cannot be rewritten, so a page whose link is its own
// access is never recorded: no replay when the visit starts on one, and the
// replay stops when the app navigates to one.
const startedOnBearerPage = isSentryBearerUrl(window.location.href);
const stopReplay = () => {
    void Sentry.getReplay()?.stop();
};

// Sentry Configuration
Sentry.init({
    dsn: configEnv.sentryDsn,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            // Links to those pages (a WhatsApp share button) are recorded as
            // empty boxes: replay masks text and inputs, not href or src.
            block: SENTRY_REPLAY_BLOCK_SELECTORS,
            // No request or response details for the API calls behind them.
            networkDetailDenyUrls: SENTRY_BEARER_URL_PATTERNS,
            // Breadcrumbs, navigations and requests in the recording. The stop
            // waits a microtask: this runs inside the SDK's history handler.
            beforeAddRecordingEvent(event) {
                if (isSentryReplayOnBearerPage(event, window.location.href)) {
                    queueMicrotask(stopReplay);
                }
                return scrubSentryReplayRecordingEvent(event);
            },
            // Backstop: an error on one of those pages never sends a replay.
            beforeErrorSampling: () => !isSentryBearerUrl(window.location.href),
        }),
        // The replay_event (its URL list, page URL and referrer) skips
        // beforeSend; event processors still see it.
        {
            name: "ReplayUrlScrub",
            processEvent: (event) =>
                event.type === "replay_event" ? scrubSentryReplayEventUrls(event) : event,
        },
        // Browser UI profiling; needs the Document-Policy: js-profiling
        // response header (vercel.json in prod, vite server headers in dev).
        Sentry.browserProfilingIntegration(),
        // console.warn/error land in Sentry Logs alongside backend logs.
        Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
    environment: configEnv.sentryEnvironment,
    // Trace every pageload/navigation; profile every traced one. Hardcoded on
    // purpose (no env), matching the backend. Lower here if quota runs dry.
    tracesSampleRate: 1.0,
    profilesSampleRate: 1.0,
    enableLogs: true,
    tracePropagationTargets: configEnv.sentryTracePropagationTargets,
    // No replay at all for a visit that starts on one of those pages (above).
    replaysSessionSampleRate: startedOnBearerPage ? 0 : configEnv.sentryReplaysSessionSampleRate,
    replaysOnErrorSampleRate: startedOnBearerPage ? 0 : configEnv.sentryReplaysOnErrorSampleRate,
    // First-line filter for the common formatted messages.
    ignoreErrors: [
        /Object Not Found Matching Id/i,
        /ResizeObserver loop/i,
        /processRandomSelector/i,
        /^null$/i,
        /^Script error\.?$/i,
    ],
    // Robust catch: drop known browser-extension / benign noise by inspecting
    // the raw rejection value, not just the formatted message.
    // Family report, parent report, certificate and parent form links carry
    // their own access in the URL (/family-report/<token>,
    // /shared/report/<token>, /certificate/<slug>, ?ti=<code> and the API
    // paths behind them). Every hook below masks those values, and only those
    // (utils/sentryUrlScrub). Session Replay is handled above.
    beforeSend(event, hint) {
        if (isIgnoredSentryEvent(event, hint)) {
            return null;
        }
        return scrubSentryEventUrls(event);
    },
    // Pageload and navigation transactions: the name is the page path, and
    // request spans hold the API URLs.
    beforeSendTransaction(event) {
        return scrubSentryEventUrls(event);
    },
    // Spans sent on their own (interaction timings) carry the page path too.
    beforeSendSpan(span) {
        return scrubSentrySpanUrls(span);
    },
    // Navigation, fetch and xhr breadcrumbs, before they are kept on the scope.
    beforeBreadcrumb(breadcrumb) {
        return scrubSentryBreadcrumbUrls(breadcrumb);
    },
    // `beforeSend` does not apply to logs - they go through their own pipeline.
    // consoleLoggingIntegration serializes every console.warn/error argument, so
    // this strips credentials/PII that slip through from unaudited call sites.
    beforeSendLog(log) {
        return scrubSentryLog(scrubSentryLogUrls(log));
    },
});

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
    <React.StrictMode>
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: "#2563eb",
                    borderRadius: 8,
                    fontFamily:
                        "'Manrope', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
                    fontSize: 14,
                    fontWeightStrong: 700,
                    colorBorder: "#e5e7eb",
                    colorBgContainer: "#ffffff",
                    colorText: "#111827",
                    colorTextSecondary: "#6b7280",
                },
                components: {
                    Card: { paddingLG: 16, borderRadiusLG: 12 },
                    Segmented: { borderRadius: 8 },
                    Typography: { fontWeightStrong: 700 },
                },
            }}
        >
            <GoogleOAuthProvider clientId={configEnv.googleClientId}>
                <Provider store={store}>
                    <AuditFormProvider>
                        <BrowserRouter>
                            <App />
                        </BrowserRouter>
                    </AuditFormProvider>
                </Provider>
                <ToastContainer />
            </GoogleOAuthProvider>
        </ConfigProvider>
    </React.StrictMode>
);
